import cookie from 'vue-cookies';
import { USER_TYPE } from '@/utils/const';
import {
  fetchAgentDashboardBundle,
  fetchAgentQuotes,
  fetchCustomerClient,
  fetchCustomerPolicyRows,
  fetchCustomerQuotes,
  fetchNotificationCount,
} from '@/services/portalCacheApi';
import { buildPolicyPreview } from '@/utils/policyRecency';

const SET_AGENT_NOT_FOUND = 'auth/SET_AGENT_NOT_FOUND';

export const CACHE_TTL = {
  quotes: 3 * 60 * 1000,
  policies: 12 * 60 * 1000,
  client: 30 * 60 * 1000,
  notifications: 5 * 60 * 1000,
  agentDashboard: 15 * 60 * 1000,
};

const emptyQuotes = () => ({
  motor: [],
  pa: [],
  health: [],
  dp: [],
  travel: [],
});

const emptyPolicies = () => ({
  motor: [],
  pa: [],
  health: [],
  dp: [],
  preview: [],
});

const defaultSnapshot = () => ({
  receiptedSales: 0,
  earnedCommissions: 0,
  commissionsPayable: 0,
  pendingRenewals: 0,
  activeQuotes: 0,
  activeQuotesValue: 0,
  closedSales: 0,
  closedSalesValue: 0,
});

const initialCustomer = () => ({
  client: null,
  policies: emptyPolicies(),
  quotes: emptyQuotes(),
  notifications: {},
  fetchedAt: {
    client: 0,
    policies: 0,
    quotes: 0,
    notifications: 0,
  },
  loading: {
    client: false,
    policies: false,
    quotes: false,
    notifications: false,
  },
  refreshing: {
    client: false,
    policies: false,
    quotes: false,
    notifications: false,
  },
});

const initialAgent = () => ({
  summary: null,
  agentNumericId: null,
  agentShortDesc: null,
  conversions: [],
  activeQuotes: [],
  quotes: emptyQuotes(),
  snapshot: defaultSnapshot(),
  fetchedAt: { dashboard: 0, quotes: 0 },
  loading: { dashboard: false, quotes: false },
  refreshing: { dashboard: false, quotes: false },
});

function isStale(fetchedAt, ttl) {
  return !fetchedAt || Date.now() - fetchedAt > ttl;
}

function resolveChannel(explicit) {
  if (explicit === 'agent' || explicit === 'customer') return explicit;
  return cookie.get(USER_TYPE) === 'agent' ? 'agent' : 'customer';
}

const SET_CLIENT = 'setClient';
const UPDATE_NOTIFICATION = 'updateNotification';
const AUTH_USER_GETTER = 'auth/authenticatedUser';

const moduleState = {
  customer: initialCustomer(),
  agent: initialAgent(),
};

const getters = {
  customerClient: (s) => s.customer.client,
  customerPoliciesPreview: (s) => s.customer.policies.preview,
  customerPoliciesLoading: (s) => s.customer.loading.policies && !s.customer.policies.preview.length,
  customerQuotes: (s) => s.customer.quotes,
  customerQuotesLoading: (s) => {
    const q = s.customer.quotes;
    const hasData = q.motor.length || q.pa.length || q.health.length || q.dp.length || q.travel.length;
    return s.customer.loading.quotes && !hasData;
  },
  customerQuotesRefreshing: (s) => s.customer.refreshing.quotes,
  customerClientLoading: (s) => s.customer.loading.client && !s.customer.client,

  agentSummary: (s) => s.agent.summary,
  agentConversions: (s) => s.agent.conversions,
  agentActiveQuotes: (s) => s.agent.activeQuotes,
  agentQuotes: (s) => s.agent.quotes,
  agentQuotesLoading: (s) => {
    const q = s.agent.quotes;
    const hasData = q.motor.length || q.pa.length || q.health.length || q.dp.length || q.travel.length;
    return s.agent.loading.quotes && !hasData;
  },
  agentQuotesRefreshing: (s) => s.agent.refreshing.quotes,
  agentSnapshot: (s) => s.agent.snapshot,
  agentNumericId: (s) => s.agent.agentNumericId,
  agentDashboardLoading: (s) => s.agent.loading.dashboard && !s.agent.summary,
  agentDashboardRefreshing: (s) => s.agent.refreshing.dashboard,
};

const mutations = {
  RESET_ALL(state) {
    state.customer = initialCustomer();
    state.agent = initialAgent();
  },
  SET_CUSTOMER_CLIENT(state, client) {
    state.customer.client = client;
    state.customer.fetchedAt.client = Date.now();
  },
  SET_CUSTOMER_POLICY_GROUPS(state, policies) {
    state.customer.policies = policies;
  },
  SET_CUSTOMER_POLICIES(state, policies) {
    state.customer.policies = policies;
    state.customer.fetchedAt.policies = Date.now();
  },
  SET_CUSTOMER_QUOTES(state, quotes) {
    state.customer.quotes = quotes;
    state.customer.fetchedAt.quotes = Date.now();
  },
  SET_CUSTOMER_NOTIFICATIONS(state, notifications) {
    state.customer.notifications = notifications;
    state.customer.fetchedAt.notifications = Date.now();
  },
  SET_CUSTOMER_LOADING(state, { key, value }) {
    state.customer.loading[key] = value;
  },
  SET_CUSTOMER_REFRESHING(state, { key, value }) {
    state.customer.refreshing[key] = value;
  },
  INVALIDATE_CUSTOMER(state, keys) {
    keys.forEach((key) => {
      if (state.customer.fetchedAt[key] !== undefined) {
        state.customer.fetchedAt[key] = 0;
      }
    });
  },
  SET_AGENT_DASHBOARD(state, payload) {
    state.agent.summary = payload.summary;
    state.agent.agentNumericId = payload.agentNumericId;
    state.agent.agentShortDesc = payload.agentShortDesc;
    state.agent.conversions = payload.conversions;
    state.agent.activeQuotes = payload.activeQuotes;
    if (payload.quotes) {
      state.agent.quotes = payload.quotes;
      state.agent.fetchedAt.quotes = Date.now();
    }
    state.agent.snapshot = payload.snapshot;
    state.agent.fetchedAt.dashboard = Date.now();
  },
  SET_AGENT_QUOTES(state, quotes) {
    state.agent.quotes = quotes;
    state.agent.fetchedAt.quotes = Date.now();
  },
  SET_AGENT_QUOTES_LOADING(state, value) {
    state.agent.loading.quotes = value;
  },
  SET_AGENT_QUOTES_REFRESHING(state, value) {
    state.agent.refreshing.quotes = value;
  },
  SET_AGENT_LOADING(state, value) {
    state.agent.loading.dashboard = value;
  },
  SET_AGENT_REFRESHING(state, value) {
    state.agent.refreshing.dashboard = value;
  },
  INVALIDATE_AGENT(state, keys) {
    keys.forEach((key) => {
      if (state.agent.fetchedAt[key] !== undefined) {
        state.agent.fetchedAt[key] = 0;
      }
    });
  },
};

async function loadCustomerClient({
  commit, rootGetters, state, force,
}) {
  const user = rootGetters[AUTH_USER_GETTER];
  if (!user) return null;

  const { fetchedAt, client } = state.customer;
  const stale = force || isStale(fetchedAt.client, CACHE_TTL.client);
  if (client && !stale) return client;

  if (client && stale) {
    commit('SET_CUSTOMER_REFRESHING', { key: 'client', value: true });
    fetchCustomerClient(user)
      .then(({ client: data, clientList }) => {
        commit('SET_CUSTOMER_CLIENT', data);
        commit(SET_CLIENT, clientList, { root: true });
      })
      .finally(() => commit('SET_CUSTOMER_REFRESHING', { key: 'client', value: false }));
    return client;
  }

  commit('SET_CUSTOMER_LOADING', { key: 'client', value: true });
  try {
    const { client: data, clientList } = await fetchCustomerClient(user);
    commit('SET_CUSTOMER_CLIENT', data);
    commit(SET_CLIENT, clientList, { root: true });
    return data;
  } finally {
    commit('SET_CUSTOMER_LOADING', { key: 'client', value: false });
  }
}

async function loadCustomerPolicies({
  commit, rootGetters, state, force,
}) {
  const user = rootGetters[AUTH_USER_GETTER];
  if (!user) return;

  const { fetchedAt, policies } = state.customer;
  const stale = force || isStale(fetchedAt.policies, CACHE_TTL.policies);
  const hasData = policies.preview.length > 0;
  if (hasData && !stale) return;

  const clientTask = state.customer.client
    ? Promise.resolve(state.customer.client)
    : loadCustomerClient({
      commit, rootGetters, state, force: false,
    });
  const groups = {
    motor: policies.motor || [],
    pa: policies.pa || [],
    travel: policies.travel || [],
    health: policies.health || [],
    dp: [],
  };
  const publish = (patch) => {
    Object.assign(groups, patch);
    commit('SET_CUSTOMER_POLICY_GROUPS', {
      ...groups,
      preview: buildPolicyPreview(groups.motor, groups.pa, groups.travel, groups.health),
    });
  };

  if (hasData && stale) {
    commit('SET_CUSTOMER_REFRESHING', { key: 'policies', value: true });
    fetchCustomerPolicyRows(user, clientTask, publish)
      .then((data) => commit('SET_CUSTOMER_POLICIES', data))
      .finally(() => commit('SET_CUSTOMER_REFRESHING', { key: 'policies', value: false }));
    return;
  }

  commit('SET_CUSTOMER_LOADING', { key: 'policies', value: true });
  try {
    const data = await fetchCustomerPolicyRows(user, clientTask, publish);
    commit('SET_CUSTOMER_POLICIES', data);
  } finally {
    commit('SET_CUSTOMER_LOADING', { key: 'policies', value: false });
  }
}

async function loadCustomerQuotes({
  commit, rootGetters, state, force,
}) {
  const user = rootGetters[AUTH_USER_GETTER];
  if (!user) return;

  const { fetchedAt, quotes } = state.customer;
  const stale = force || isStale(fetchedAt.quotes, CACHE_TTL.quotes);
  const hasData = quotes.motor.length || quotes.pa.length || quotes.health.length
    || quotes.dp.length || quotes.travel.length;

  if (hasData && !stale) return;

  if (hasData && stale) {
    commit('SET_CUSTOMER_REFRESHING', { key: 'quotes', value: true });
    fetchCustomerQuotes(user)
      .then((data) => commit('SET_CUSTOMER_QUOTES', data))
      .finally(() => commit('SET_CUSTOMER_REFRESHING', { key: 'quotes', value: false }));
    return;
  }

  commit('SET_CUSTOMER_LOADING', { key: 'quotes', value: true });
  try {
    const data = await fetchCustomerQuotes(user);
    commit('SET_CUSTOMER_QUOTES', data);
  } finally {
    commit('SET_CUSTOMER_LOADING', { key: 'quotes', value: false });
  }
}

async function loadCustomerNotifications({
  commit, rootGetters, state, force,
}) {
  if (cookie.get(USER_TYPE) === 'agent') return;

  const user = rootGetters[AUTH_USER_GETTER];
  if (!user) return;

  const { fetchedAt } = state.customer;
  const stale = force || isStale(fetchedAt.notifications, CACHE_TTL.notifications);
  const hasData = Object.keys(state.customer.notifications).length > 0;
  if (hasData && !stale) return;

  if (hasData && stale) {
    commit('SET_CUSTOMER_REFRESHING', { key: 'notifications', value: true });
    fetchNotificationCount(user)
      .then((data) => {
        commit('SET_CUSTOMER_NOTIFICATIONS', data);
        commit(UPDATE_NOTIFICATION, data, { root: true });
      })
      .finally(() => commit('SET_CUSTOMER_REFRESHING', { key: 'notifications', value: false }));
    return;
  }

  commit('SET_CUSTOMER_LOADING', { key: 'notifications', value: true });
  try {
    const data = await fetchNotificationCount(user);
    commit('SET_CUSTOMER_NOTIFICATIONS', data);
    commit(UPDATE_NOTIFICATION, data, { root: true });
  } finally {
    commit('SET_CUSTOMER_LOADING', { key: 'notifications', value: false });
  }
}

async function loadAgentDashboard({
  commit, rootGetters, state, force,
}) {
  const user = rootGetters[AUTH_USER_GETTER];
  if (!user) return;

  const { fetchedAt, summary } = state.agent;
  const stale = force || isStale(fetchedAt.dashboard, CACHE_TTL.agentDashboard);
  if (summary && !stale) return;

  if (summary && stale) {
    commit('SET_AGENT_REFRESHING', true);
    fetchAgentDashboardBundle(user)
      .then((data) => {
        commit(SET_AGENT_NOT_FOUND, data.agentNotFound, { root: true });
        if (data.agentNumericId != null) {
          localStorage.setItem('agentNumericId', String(data.agentNumericId));
        }
        if (data.agentShortDesc) {
          localStorage.setItem('agentId', data.agentShortDesc);
        }
        commit('SET_AGENT_DASHBOARD', data);
      })
      .finally(() => commit('SET_AGENT_REFRESHING', false));
    return;
  }

  commit('SET_AGENT_LOADING', true);
  try {
    const data = await fetchAgentDashboardBundle(user);
    commit(SET_AGENT_NOT_FOUND, data.agentNotFound, { root: true });
    if (data.agentNumericId != null) {
      localStorage.setItem('agentNumericId', String(data.agentNumericId));
    }
    if (data.agentShortDesc) {
      localStorage.setItem('agentId', data.agentShortDesc);
    }
    commit('SET_AGENT_DASHBOARD', data);
  } finally {
    commit('SET_AGENT_LOADING', false);
  }
}

async function loadAgentQuotes({
  commit, rootGetters, state, force,
}) {
  const user = rootGetters[AUTH_USER_GETTER];
  if (!user) return;

  const { fetchedAt, quotes } = state.agent;
  const hasData = quotes.motor.length || quotes.pa.length || quotes.health.length || quotes.dp.length || quotes.travel.length;
  const stale = force || isStale(fetchedAt.quotes, CACHE_TTL.quotes);
  if (hasData && !stale) return;

  if (hasData && stale) {
    commit('SET_AGENT_QUOTES_REFRESHING', true);
    fetchAgentQuotes(user, {
      agentNumericId: state.agent.agentNumericId,
      agentShortDesc: state.agent.agentShortDesc,
    })
      .then((data) => {
        commit('SET_AGENT_QUOTES', data);
      })
      .finally(() => commit('SET_AGENT_QUOTES_REFRESHING', false));
    return;
  }

  commit('SET_AGENT_QUOTES_LOADING', true);
  try {
    const data = await fetchAgentQuotes(user, {
      agentNumericId: state.agent.agentNumericId,
      agentShortDesc: state.agent.agentShortDesc,
    });
    commit('SET_AGENT_QUOTES', data);
  } finally {
    commit('SET_AGENT_QUOTES_LOADING', false);
  }
}

const actions = {
  clearAll({ commit }) {
    commit('RESET_ALL');
  },

  async bootstrap({ dispatch }, { channel, force = false } = {}) {
    const resolved = resolveChannel(channel);
    if (resolved === 'agent') {
      await Promise.all([
        dispatch('fetchAgentDashboard', { force }),
        dispatch('fetchAgentQuotes', { force }),
      ]);
      return;
    }
    await Promise.all([
      dispatch('fetchCustomerClient', { force }),
      dispatch('fetchCustomerPolicies', { force }),
      dispatch('fetchCustomerQuotes', { force }),
    ]);
  },

  fetchCustomerClient: (ctx, opts = {}) => loadCustomerClient({ ...ctx, ...opts }),
  fetchCustomerPolicies: (ctx, opts = {}) => loadCustomerPolicies({ ...ctx, ...opts }),
  fetchCustomerQuotes: (ctx, opts = {}) => loadCustomerQuotes({ ...ctx, ...opts }),
  fetchCustomerNotifications: (ctx, opts = {}) => loadCustomerNotifications({ ...ctx, ...opts }),
  fetchAgentDashboard: (ctx, opts = {}) => loadAgentDashboard({ ...ctx, ...opts }),
  fetchAgentQuotes: (ctx, opts = {}) => loadAgentQuotes({ ...ctx, ...opts }),

  invalidate({ commit, dispatch }, {
    channel, keys = [], refresh = true, force = true,
  } = {}) {
    const resolved = resolveChannel(channel);
    if (resolved === 'agent') {
      commit('INVALIDATE_AGENT', ['dashboard', 'quotes']);
      if (refresh) {
        return Promise.all([
          dispatch('fetchAgentDashboard', { force }),
          dispatch('fetchAgentQuotes', { force }),
        ]);
      }
      return Promise.resolve();
    }

    const customerKeys = keys.length ? keys : ['client', 'policies', 'quotes', 'notifications'];
    commit('INVALIDATE_CUSTOMER', customerKeys);

    if (!refresh) return Promise.resolve();

    const tasks = [];
    if (customerKeys.includes('client')) tasks.push(dispatch('fetchCustomerClient', { force }));
    if (customerKeys.includes('policies')) tasks.push(dispatch('fetchCustomerPolicies', { force }));
    if (customerKeys.includes('quotes')) tasks.push(dispatch('fetchCustomerQuotes', { force }));
    if (customerKeys.includes('notifications')) tasks.push(dispatch('fetchCustomerNotifications', { force }));
    return Promise.all(tasks);
  },
};

export default {
  namespaced: true,
  state: moduleState,
  getters,
  mutations,
  actions,
};
